import { useState, useEffect } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import CollapsibleCard from 'src/careflow/common/CollapsibleCard';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { ReferralDto } from 'src/crs/referral/dto/referral.dto';
import { ReferralTabPanel, NoResultText, LoadingComponent } from './components';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { ReferralService } from 'src/crs/referral/services/ReferralService';

export interface RelatedReferralsProps {
  isOpen: boolean;
  referralIds: string[];
  externalLoading: boolean;
  patientId: string;
}

const RelatedReferrals = ({
  isOpen,
  referralIds,
  externalLoading,
  patientId,
}: RelatedReferralsProps) => {
  const { i18n } = useLocales();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [referrals, setReferrals] = useState<ReferralDto[]>([]);

  useEffect(() => {
    const getReferralRecords = async () => {
      if (!referralIds.length) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const paginateQuery: PaginateQuery = {
        limit: 999,
        filter: {
          // fhirId: referralIds,
          fhirId: `$in:${referralIds.join(',')}`,
        },
      };

      const referrals = await ReferralService.getAll(paginateQuery);
      setIsLoading(false);
      if (referrals?.data?.length) setReferrals(referrals.data);
    };
    getReferralRecords();
  }, [referralIds]);

  return (
    <CollapsibleCard
      title={i18n('case.details.relatedReferrals.title', 'crs')}
      titleSx={{ ml: 1 }}
      isOpenExternal={isOpen}
      margin={2}
    >
      <Box>
        {!isLoading && !externalLoading ? (
          referrals.length ? (
            <>
              <Tabs
                value={selectedTab}
                variant={'scrollable'}
                onChange={(_, newSelectedTab) => {
                  setSelectedTab(newSelectedTab);
                }}
                TabIndicatorProps={{
                  sx: {
                    bottom: 5,
                  },
                }}
              >
                {referrals.map((_, index) => (
                  <Tab
                    sx={{ textAlign: 'left' }}
                    label={`Referral ${index + 1}`}
                    key={`referralTab${String(index)}`}
                  />
                ))}
              </Tabs>

              {referrals.map((referral, index) => (
                <ReferralTabPanel
                  referral={referral}
                  index={index}
                  key={`referralTabPanel${String(index)}`}
                  selectedTab={selectedTab}
                  patientId={patientId}
                />
              ))}
            </>
          ) : (
            <NoResultText text="No referrals related found" />
          )
        ) : (
          <LoadingComponent />
        )}
      </Box>
    </CollapsibleCard>
  );
};

export default RelatedReferrals;
